import type {Ref} from 'vue';

import {
  buildMaterialColorMap,
  colorContrast,
  contrastRatio,
  hexColorWithAlpha,
  mixHexColors,
  mixHexColorsOklab,
  normalizeHexColor,
  relativeLuminance,
} from '@/use/colorUtils';

const DEFAULT_BODY_BACKGROUND = '#ffffff';
const DEFAULT_BORDER_COLOR = '#464a4e';
const DEFAULT_MENU_BACKGROUND = '#b1b5b9';
const DEFAULT_MENU_TEXT = '#000000';
const DARK_MIX_COLOR = '#0a0a0b';
const MATERIAL_LIGHT_MIX_WEIGHT = 0.125;
const MATERIAL_SECONDARY_LUMINANCE_THRESHOLD = relativeLuminance('#fafafa') ?? 0.955973;

type ThemeValue = Readonly<Ref<string | null>>;

interface ClientThemeOptions {
  clientLogoURL: ThemeValue,
  clientBodyBackgroundColor: ThemeValue,
  clientBorderColor: ThemeValue,
  clientMenuBackgroundColor: ThemeValue,
  clientMenuTextColor: ThemeValue,
}

function buildAndSetAlternateColors(
  root: HTMLElement,
  themeMenuBackgroundColor: string,
  themeMenuTextColor: string,
) {
  const themeMenuOpposite = colorContrast(themeMenuBackgroundColor);
  const materialColors = buildMaterialColorMap(themeMenuBackgroundColor);
  const themeMenuBackgroundLuminance = relativeLuminance(themeMenuBackgroundColor);
  const navigationBackgroundHover = mixHexColorsOklab(themeMenuBackgroundColor, themeMenuOpposite, 0.82)
      ?? themeMenuBackgroundColor;
  const navigationBackgroundActive = mixHexColorsOklab(themeMenuBackgroundColor, themeMenuOpposite, 0.72)
      ?? themeMenuBackgroundColor;
  const navigationBackgroundDark = mixHexColorsOklab(themeMenuBackgroundColor, themeMenuOpposite, 0.82)
    ?? themeMenuBackgroundColor;
  const navigationSecondaryBackground = materialColors && themeMenuBackgroundLuminance !== null
    ? themeMenuBackgroundLuminance <= MATERIAL_SECONDARY_LUMINANCE_THRESHOLD
      ? materialColors['300']
      : mixHexColors(
        DARK_MIX_COLOR,
        themeMenuBackgroundColor,
        MATERIAL_LIGHT_MIX_WEIGHT,
      ) ?? themeMenuBackgroundColor
    : themeMenuBackgroundColor;
  const secondaryBackgroundOpposite = colorContrast(navigationSecondaryBackground);
  const proposedSecondaryText = mixHexColorsOklab(
    secondaryBackgroundOpposite,
    themeMenuTextColor,
    0.74,
  ) ?? secondaryBackgroundOpposite;
  const navigationSecondaryText = (contrastRatio(
    navigationSecondaryBackground,
    proposedSecondaryText,
  ) ?? 0) >= 4.5
    ? proposedSecondaryText
    : secondaryBackgroundOpposite;
  const navigationSecondaryTextContrast = colorContrast(navigationSecondaryText);
  const navigationSecondaryBackgroundContrast = colorContrast(navigationSecondaryBackground);
  const navigationDarkHover = mixHexColors(navigationBackgroundDark, '#ffffff', 0.8)
    ?? navigationBackgroundDark;

  root.style.setProperty('--nav-background-hover', navigationBackgroundHover);
  root.style.setProperty('--nav-background-active', navigationBackgroundActive);
  root.style.setProperty('--nav-background-dark', navigationBackgroundDark);
  root.style.setProperty('--nav-background-dark-hover', navigationDarkHover);
  root.style.setProperty('--nav-secondary-background', navigationSecondaryBackground);
  root.style.setProperty('--nav-secondary-text', navigationSecondaryText);
  root.style.setProperty(
    '--nav-secondary-background-hover',
    hexColorWithAlpha(navigationSecondaryTextContrast, 0.3) ?? navigationSecondaryTextContrast,
  );
  root.style.setProperty(
    '--nav-secondary-background-active',
    hexColorWithAlpha(navigationSecondaryTextContrast, 0.2) ?? navigationSecondaryTextContrast,
  );
  root.style.setProperty(
    '--nav-secondary-text-muted',
    hexColorWithAlpha(navigationSecondaryBackgroundContrast, 0.7)
      ?? navigationSecondaryBackgroundContrast,
  );
  root.style.setProperty(
    '--nav-secondary-border-color',
    hexColorWithAlpha(navigationSecondaryTextContrast, 0.3) ?? navigationSecondaryTextContrast,
  );
}

export default function useClientTheme({
  clientLogoURL,
  clientBodyBackgroundColor,
  clientBorderColor,
  clientMenuBackgroundColor,
  clientMenuTextColor,
}: ClientThemeOptions) {
  function setRootThemeVariables() {
    const root = document.documentElement;
    const themeBodyBackgroundColor = normalizeHexColor(clientBodyBackgroundColor.value ?? '')
      ?? DEFAULT_BODY_BACKGROUND;
    const themeBorderColor = normalizeHexColor(clientBorderColor.value ?? '')
      ?? DEFAULT_BORDER_COLOR;
    const themeMenuBackgroundColor = normalizeHexColor(clientMenuBackgroundColor.value ?? '')
      ?? DEFAULT_MENU_BACKGROUND;
    const themeMenuTextColor = normalizeHexColor(clientMenuTextColor.value ?? '')
      ?? DEFAULT_MENU_TEXT;

    root.style.setProperty('--theme-body-background', themeBodyBackgroundColor);
    root.style.setProperty('--theme-border-color', themeBorderColor);
    root.style.setProperty('--theme-menu-background-color', themeMenuBackgroundColor);
    root.style.setProperty('--theme-menu-text-color', themeMenuTextColor);
    root.style.setProperty('--nav-background', themeMenuBackgroundColor);
    root.style.setProperty('--nav-text', themeMenuTextColor);
    root.style.setProperty('--nav-border-color', themeBorderColor);

    if (clientLogoURL.value) {
      root.style.setProperty('--theme-logo-url', clientLogoURL.value);
    } else {
      root.style.removeProperty('--theme-logo-url');
    }

    buildAndSetAlternateColors(root, themeMenuBackgroundColor, themeMenuTextColor);
  }

  return {
    setRootThemeVariables,
  };
}
